import React, { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import FirebaseService from "../../actions/firebase/FirebaseActions";
import { UpdateRegistration } from "../../models/registration/Registration";
import { BackButton } from "../common/button/Buttons";
import { ContentLayout } from "../common/Layout";
import { useRegistration } from "../../hooks/useRegistration";
import { useAuthentication } from "../../hooks/useAuthentication";
import RegistrationEditor, { REGISTRATION_FORM_ID } from "./RegistrationEditor";
import { useFormSchema } from "../../hooks/useFormSchema";
import { trackEvent } from "../../helper/analytics";
import { useUserIdParam } from "../../hooks/useUserIdParam";
import { useRegistrationIdParam } from "../../hooks/useRegistrationIdParam";

interface Props {
  isAuthenticated: boolean;
}
const UpdateRegistrationEditor: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const userId = useUserIdParam();
  const registrationId = useRegistrationIdParam();

  const navigate = useNavigate();
  const [isSaving, setSaving] = useState(false);
  const [registration, registrationLoading, registrationError] = useRegistration(userId, registrationId);
  const [formSchema, fetchFormSchema] = useFormSchema(userId);
  useAuthentication(isAuthenticated);

  if (registrationLoading || !formSchema) return <p>Daten werden geladen</p>;
  else if (registrationError || !registration) return <p>Fehler beim Laden der Daten</p>;

  const saveRegistration = async (formData: unknown): Promise<void> => {
    const registration: UpdateRegistration = {
      id: registrationId,
      formData: formData
    };

    setSaving(true);
    try {
      await FirebaseService.updateRegistration(userId, registration);
      toast.success("Die Änderungen wurden gespeichert");
      fetchFormSchema();
      navigate(`/user/${userId}/registrations`);
    } catch (e) {
      console.log(e);
      toast.error(
        "Es ist ein Fehler beim Speichern der Daten aufgetreten. Bitte laden Sie die Applikation erneut und versuchen Sie es noch einmal."
      );
    } finally {
      setSaving(false);
      trackEvent("update-registration");
    }
  };

  return (
    <ContentLayout
      title={"Anmeldung bearbeiten"}
      siteTitle={"Spielgruppe Online - Anmeldung bearbeiten"}
      maxWidth={860}>
      <div className="editor-toolbar">
        <BackButton to={`/user/${userId}/registrations`} />
        <button
          type="submit"
          form={REGISTRATION_FORM_ID}
          disabled={isSaving}
          className="btn btn-primary editor-toolbar__save">
          Speichern
        </button>
      </div>
      <section className="panel">
        <div className="panel__body">
          <RegistrationEditor
            formSchema={formSchema}
            formData={registration.formData}
            isSaving={isSaving}
            onSubmit={saveRegistration}
          />
        </div>
        <div className="panel__bar">
          <span>
            Erstellt: {registration.creation.createdAt.toLocaleString()} · Zuletzt aktualisiert:{" "}
            {registration.creation.updatedAt.toLocaleString()}
          </span>
        </div>
      </section>
    </ContentLayout>
  );
};

export default UpdateRegistrationEditor;
